/**
 * ScheduledSendsDialog — lists the user's pending scheduled sends and lets them
 * cancel one. Backed by the one-shot scheduled_actions created by "Schedule send".
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useScheduledSends, useCancelScheduledSend } from '../hooks/useMailbox';

interface ScheduledSendsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ScheduledSendsDialog({ open, onOpenChange }: ScheduledSendsDialogProps) {
  const { data: scheduled, isLoading } = useScheduledSends(open);
  const cancel = useCancelScheduledSend();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Scheduled sends</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !scheduled || scheduled.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No scheduled messages.</p>
        ) : (
          <ul className="space-y-2">
            {scheduled.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-3 rounded border px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{s.subject || '(no subject)'}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    To {s.to.join(', ') || '—'}
                    {s.sendAt ? ` · ${new Date(s.sendAt).toLocaleString()}` : ''}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={cancel.isPending}
                  onClick={() => cancel.mutate(s.id)}
                >
                  Cancel
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
